import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { hasAtLeastRole, type Product, type UpdateProductGestionInput } from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { useAuth } from '../../auth/auth-context';

const PRODUCTS_QUERY_KEY = ['products'] as const;
const FAMILIES_QUERY_KEY = ['families', 'validees'] as const;
const TAILLE_PAGE = 100;

type FiltrePerimetre = 'tous' | 'dedans' | 'dehors';

/** Seuls les attributs de gestion sont editables (regle metier 13). */
interface EditionGestion {
  id: string;
  codpro: string;
  familyId: string;
  horsPerimetreControle: boolean;
  commentaire: string;
}

export function ArticlesPage(): ReactElement {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const peutModifier = user ? hasAtLeastRole(user.role, 'OPERATEUR') : false;

  const [recherche, setRecherche] = useState('');
  const [perimetre, setPerimetre] = useState<FiltrePerimetre>('tous');
  const [page, setPage] = useState(1);
  const [tri, setTri] = useState<SortingState>([{ id: 'codpro', desc: false }]);
  const [edition, setEdition] = useState<EditionGestion | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const listeQuery = useQuery({
    queryKey: [...PRODUCTS_QUERY_KEY, recherche, perimetre, page],
    queryFn: () =>
      api.listProducts({
        search: recherche || undefined,
        inScope: perimetre === 'tous' ? undefined : perimetre === 'dedans',
        page,
        pageSize: TAILLE_PAGE,
      }),
  });

  const famillesQuery = useQuery({
    queryKey: FAMILIES_QUERY_KEY,
    queryFn: () => api.listFamilies({ statut: 'VALIDEE', pageSize: 200 }),
    enabled: peutModifier,
  });

  const modification = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateProductGestionInput }) =>
      api.updateProductGestion(id, patch),
    onSuccess: (article) => {
      setEdition(null);
      setErreur(null);
      setMessage(`Attributs de gestion mis à jour pour l'article ${article.codpro}.`);
      void queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['families'] });
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const colonnes = useMemo<ColumnDef<Product>[]>(
    () => [
      { accessorKey: 'codpro', header: 'Code article' },
      { accessorKey: 'designation', header: 'Désignation' },
      { accessorKey: 'codalc', header: 'Code alcool', cell: (info) => info.getValue() ?? '—' },
      { accessorKey: 'degalc', header: 'Degré (% vol)', cell: (info) => info.getValue() ?? '—' },
      {
        accessorKey: 'isInScope',
        header: 'Périmètre douane',
        cell: (info) => (
          <span
            className={`etiquette ${info.getValue() ? 'etiquette--actif' : 'etiquette--inactif'}`}
          >
            {info.getValue() ? 'Dans le périmètre' : 'Hors périmètre'}
          </span>
        ),
      },
      {
        accessorKey: 'familyLibelle',
        header: 'Famille',
        cell: ({ row }) =>
          row.original.familyId ? (
            (row.original.familyLibelle ?? 'Famille sans libellé')
          ) : (
            <span className="sous-titre">—</span>
          ),
      },
      {
        accessorKey: 'horsPerimetreControle',
        header: 'Exclu du contrôle',
        cell: (info) =>
          info.getValue() ? <span className="etiquette etiquette--inactif">Exclu</span> : '—',
      },
      {
        accessorKey: 'createdOnTheFly',
        header: 'Origine',
        cell: (info) => (info.getValue() ? 'Créé à la volée' : 'Fiche article'),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) =>
          peutModifier ? (
            <button
              type="button"
              className="bouton bouton--lien"
              onClick={() => ouvrirEdition(row.original)}
            >
              Gestion
            </button>
          ) : (
            <span className="sous-titre">Lecture seule</span>
          ),
      },
    ],
    [peutModifier],
  );

  const table = useReactTable({
    data: listeQuery.data?.items ?? [],
    columns: colonnes,
    state: { sorting: tri },
    onSortingChange: setTri,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const articleEnEdition =
    edition && listeQuery.data
      ? (listeQuery.data.items.find((article) => article.id === edition.id) ?? null)
      : null;

  function ouvrirEdition(article: Product): void {
    setErreur(null);
    setMessage(null);
    setEdition({
      id: article.id,
      codpro: article.codpro,
      familyId: article.familyId ?? '',
      horsPerimetreControle: article.horsPerimetreControle,
      commentaire: article.commentaire ?? '',
    });
  }

  function enregistrer(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!edition) return;
    modification.mutate({
      id: edition.id,
      patch: {
        familyId: edition.familyId === '' ? null : edition.familyId,
        horsPerimetreControle: edition.horsPerimetreControle,
        commentaire: edition.commentaire.trim() === '' ? null : edition.commentaire.trim(),
      },
    });
  }

  function changerRecherche(valeur: string): void {
    setRecherche(valeur);
    setPage(1);
  }

  function changerPerimetre(valeur: FiltrePerimetre): void {
    setPerimetre(valeur);
    setPage(1);
  }

  const total = listeQuery.data?.total ?? 0;
  const totalPages = listeQuery.data?.totalPages ?? 0;

  return (
    <>
      <h1>Référentiels &mdash; Articles</h1>
      <p className="sous-titre">
        Référentiel issu de la fiche article. Les champs importés sont en{' '}
        <strong>lecture seule</strong> et ne sont rafraîchis que par les imports. Seuls les
        attributs de gestion (famille, exclusion du contrôle, commentaire) sont modifiables, et
        chaque modification est tracée.
      </p>

      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}
      {message && <p className="alerte alerte--succes">{message}</p>}

      <div className="carte">
        <div className="barre-actions">
          <label className="champ" htmlFor="recherche-article">
            <span>Rechercher</span>
            <input
              id="recherche-article"
              type="search"
              placeholder="Code, désignation ou code alcool"
              value={recherche}
              onChange={(event) => changerRecherche(event.target.value)}
            />
          </label>
          <label className="champ" htmlFor="filtre-perimetre">
            <span>Périmètre douane</span>
            <select
              id="filtre-perimetre"
              value={perimetre}
              onChange={(event) => changerPerimetre(event.target.value as FiltrePerimetre)}
            >
              <option value="tous">Tous les articles</option>
              <option value="dedans">Dans le périmètre</option>
              <option value="dehors">Hors périmètre</option>
            </select>
          </label>
        </div>

        {articleEnEdition && edition && (
          <FicheArticle
            article={articleEnEdition}
            edition={edition}
            familles={famillesQuery.data?.items ?? []}
            enCours={modification.isPending}
            onChange={setEdition}
            onSubmit={enregistrer}
            onAnnuler={() => setEdition(null)}
          />
        )}

        {listeQuery.isPending && <p className="chargement">Chargement du référentiel...</p>}
        {listeQuery.isError && (
          <p className="alerte alerte--erreur">{messageErreur(listeQuery.error)}</p>
        )}

        {listeQuery.data && (
          <>
            <table className="tableau">
              <caption className="sous-titre">
                {total} article(s) &mdash; page {page} sur {Math.max(totalPages, 1)}
              </caption>
              <thead>
                {table.getHeaderGroups().map((groupe) => (
                  <tr key={groupe.id}>
                    {groupe.headers.map((entete) => (
                      <th key={entete.id} scope="col">
                        {entete.column.getCanSort() ? (
                          <button
                            type="button"
                            className="bouton bouton--lien"
                            onClick={entete.column.getToggleSortingHandler()}
                          >
                            {flexRender(entete.column.columnDef.header, entete.getContext())}
                            {{ asc: ' ▲', desc: ' ▼' }[entete.column.getIsSorted() as string] ?? ''}
                          </button>
                        ) : (
                          flexRender(entete.column.columnDef.header, entete.getContext())
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cellule) => (
                      <td key={cellule.id}>
                        {flexRender(cellule.column.columnDef.cell, cellule.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {total === 0 && (
              <p className="alerte alerte--info">
                Aucun article. Importez la fiche article depuis l&apos;écran Imports : le
                référentiel ne se saisit pas à la main.
              </p>
            )}

            <div className="barre-actions">
              <button
                type="button"
                className="bouton bouton--secondaire"
                disabled={page <= 1}
                onClick={() => setPage((courante) => courante - 1)}
              >
                Page précédente
              </button>
              <button
                type="button"
                className="bouton bouton--secondaire"
                disabled={page >= totalPages}
                onClick={() => setPage((courante) => courante + 1)}
              >
                Page suivante
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

interface FicheArticleProps {
  article: Product;
  edition: EditionGestion;
  familles: Array<{ id: string; libelle: string | null; members: Array<{ codpro: string }> }>;
  enCours: boolean;
  onChange: (edition: EditionGestion) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onAnnuler: () => void;
}

/**
 * Fiche article : les faits importes sont presentes en lecture seule, avec la date
 * du dernier import les ayant rafraichis ; seul le bloc « Gestion » est editable.
 */
function FicheArticle({
  article,
  edition,
  familles,
  enCours,
  onChange,
  onSubmit,
  onAnnuler,
}: FicheArticleProps): ReactElement {
  return (
    <div className="carte">
      <h2>Article {article.codpro}</h2>

      <h3>Données importées</h3>
      <p className="sous-titre">
        Ces champs proviennent de la fiche article et ne sont pas modifiables ici.{' '}
        {article.lastImportedAt
          ? `Dernier rafraîchissement : ${new Date(article.lastImportedAt).toLocaleString('fr-FR')}.`
          : "Cet article a été créé à la volée par un import de mouvements : il n'a jamais été rafraichi par une fiche article."}
      </p>
      <dl className="fiche">
        <div>
          <dt>Code article</dt>
          <dd>{article.codpro}</dd>
        </div>
        <div>
          <dt>Désignation</dt>
          <dd>{article.designation}</dd>
        </div>
        <div>
          <dt>Code matière</dt>
          <dd>{article.codmtr ?? '—'}</dd>
        </div>
        <div>
          <dt>Code alcool</dt>
          <dd>{article.codalc ?? '—'}</dd>
        </div>
        <div>
          <dt>Degré (% vol)</dt>
          <dd>{article.degalc ?? '—'}</dd>
        </div>
        <div>
          <dt>Périmètre douane</dt>
          <dd>{article.isInScope ? 'Dans le périmètre' : 'Hors périmètre'}</dd>
        </div>
        <div>
          <dt>Origine</dt>
          <dd>{article.createdOnTheFly ? 'Créé à la volée' : 'Fiche article'}</dd>
        </div>
      </dl>

      <h3>Gestion</h3>
      <form onSubmit={onSubmit} noValidate>
        <div className="barre-actions">
          <label className="champ" htmlFor="gestion-famille">
            <span>Famille d&apos;équivalence</span>
            <select
              id="gestion-famille"
              value={edition.familyId}
              onChange={(event) => onChange({ ...edition, familyId: event.target.value })}
            >
              <option value="">Aucune</option>
              {familles.map((famille) => (
                <option key={famille.id} value={famille.id}>
                  {famille.libelle ?? famille.members.map((m) => m.codpro).join(' / ')}
                </option>
              ))}
            </select>
          </label>
          <label className="champ" htmlFor="gestion-hors-controle">
            <span>Hors périmètre de contrôle</span>
            <select
              id="gestion-hors-controle"
              value={edition.horsPerimetreControle ? 'oui' : 'non'}
              onChange={(event) =>
                onChange({ ...edition, horsPerimetreControle: event.target.value === 'oui' })
              }
            >
              <option value="non">Non</option>
              <option value="oui">Oui</option>
            </select>
          </label>
          <label className="champ" htmlFor="gestion-commentaire">
            <span>Commentaire</span>
            <input
              id="gestion-commentaire"
              type="text"
              value={edition.commentaire}
              onChange={(event) => onChange({ ...edition, commentaire: event.target.value })}
            />
          </label>
          <button type="submit" className="bouton" disabled={enCours}>
            Enregistrer
          </button>
          <button type="button" className="bouton bouton--secondaire" onClick={onAnnuler}>
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}
